// src/pages/misc/public/ContactPage.tsx
// [HANDOFF] CONTACT PAGE (/contacto). Two blocks: (1) WhatsApp: editable title + message, live preview, and a button that opens
//   https://wa.me/<phone>?text=... with the title in bold (see wa.ts); (2) Email: name / email / message that opens the visitor's mail app
//   (mailto) addressed to MAIL below. Nothing is sent to or stored on a server.
// [SIGNUP-LOOKALIKE] Not a sign-up. It is only a contact form.
// [HANDOFF] The Socios page links here with `?tema=socio`. When that is present the title and message start pre-filled for the referral
//   program ("Quiero el programa de referidos"); without it they say "Quiero ver Ventaje". Only the first load reads the parameter.
// [URL-RENAME] Route /contacto -> /contact (collides with the older /contact page, decide first). Parameter ?tema=socio -> ?topic=partner
//   (change the one read below and the link in SociosPage.tsx together).
// [VERIFY] The email address is typed here (MAIL below) and again in pages.test.js, which checks it; the phone comes from wa.ts.
// WU-164 D3 (QA-F09): a send with the email or the message blank shows a role="alert" line under the button naming what is missing,
//   and marks each blank field aria-invalid; both follow the fields as the visitor types (docs/manifest/WU-164.md SESSION 1 RECORD D3).
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MessageCircle } from 'lucide-react';
import PublicShell from './PublicShell';
import { WA_DISPLAY, waLink, waText } from './wa';

const MAIL = 'Merivilla@merivilla.com';

// The line an empty send shows under the button (QA-F09): it names each blank field and goes as the visitor fills them.
const missingNote = (emailMissing: boolean, msgMissing: boolean) => {
  if (emailMissing && msgMissing) return 'Faltan tu correo y tu mensaje.';
  if (emailMissing) return 'Falta tu correo.';
  if (msgMissing) return 'Falta tu mensaje.';
  return '';
};

const ContactPage = () => {
  const [params] = useSearchParams();
  const fromPartner = params.get('tema') === 'socio';
  const [title, setTitle] = useState(fromPartner ? 'Quiero el programa de referidos' : 'Quiero ver Ventaje');
  const [body, setBody] = useState(
    fromPartner
      ? 'Hola, Michael. Quiero platicar del programa de referidos y a quién te puedo presentar.'
      : 'Hola. Quiero una llamada de 15 minutos para ver Ventaje con mis leads.',
  );
  const [mail, setMail] = useState({ name: '', email: '', msg: '' });
  const [sent, setSent] = useState(false);
  const [triedSend, setTriedSend] = useState(false); // an empty send was tried: the note and aria-invalid follow the fields from then on

  const preview = useMemo(() => waText(title, body), [title, body]);
  const emailMissing = triedSend && !mail.email.trim();
  const msgMissing = triedSend && !mail.msg.trim();
  const note = missingNote(emailMissing, msgMissing);

  const sendMail = () => {
    if (!mail.email.trim() || !mail.msg.trim()) {
      setTriedSend(true);
      return;
    }
    const subject = encodeURIComponent(mail.name ? `Ventaje — ${mail.name}` : 'Mensaje desde Ventaje');
    const text = encodeURIComponent(`${mail.name}\n${mail.email}\n\n${mail.msg}`);
    window.location.href = `mailto:${MAIL}?subject=${subject}&body=${text}`;
    setSent(true);
  };

  return (
    <PublicShell
      pageTitle="Contacto"
      kicker="Contacto"
      title={<>Dos formas de <b>escribirnos.</b></>}
      lead={`WhatsApp al ${WA_DISPLAY}, o un correo. Los dos llegan a Ventaje.`}
    >
      <section className="pub-block">
        <h2><MessageCircle size={18} /> WhatsApp</h2>
        <p className="pub-note">El título va en negritas. Abajo escribes lo que quieras. El botón arma el mensaje y lo abre en WhatsApp, listo para enviar.</p>
        <label className="pub-label" htmlFor="wa-title">Título</label>
        <input id="wa-title" className="pub-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        <label className="pub-label" htmlFor="wa-body">Mensaje</label>
        <textarea id="wa-body" className="pub-area" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
        <div className="pub-preview">
          <span>Así se va a ver</span>
          {title.trim() ? <p><b>{title.trim()}</b></p> : null}
          {body.trim().split('\n').map((line) => <p key={line}>{line || '\u00a0'}</p>)}
        </div>
        <a
          className="btn-gold inline-flex items-center justify-center gap-2 min-h-[52px] px-6 rounded-[10px] text-[12px] font-bold uppercase tracking-[0.1em]"
          href={waLink(preview)}
          target="_blank"
          rel="noreferrer"
        >
          Enviar por WhatsApp
        </a>
      </section>

      <section className="pub-block">
        <h2><Mail size={18} /> Correo</h2>
        <p className="pub-note">Esto se envía directo al correo de Ventaje. Se abre tu app de correo con el mensaje ya escrito.</p>
        {sent ? (
          <p className="pub-lead">Listo. Revisa la ventana de correo y dale enviar.</p>
        ) : (
          <>
            <label className="pub-label" htmlFor="c-name">Nombre</label>
            <input id="c-name" className="pub-input" value={mail.name} onChange={(e) => setMail({ ...mail, name: e.target.value })} />
            <label className="pub-label" htmlFor="c-email">Tu correo</label>
            <input id="c-email" className="pub-input" type="email" value={mail.email} aria-invalid={emailMissing} onChange={(e) => setMail({ ...mail, email: e.target.value })} />
            <label className="pub-label" htmlFor="c-msg">Mensaje</label>
            <textarea id="c-msg" className="pub-area" rows={4} value={mail.msg} aria-invalid={msgMissing} onChange={(e) => setMail({ ...mail, msg: e.target.value })} />
            <button type="button" className="btn-gold inline-flex items-center justify-center min-h-[52px] px-6 rounded-[10px] text-[12px] font-bold uppercase tracking-[0.1em]" onClick={sendMail}>
              Enviar correo
            </button>
            {note ? <p className="pub-note" role="alert">{note}</p> : null}
          </>
        )}
      </section>
    </PublicShell>
  );
};

export default ContactPage;
